import { type FC, useState, type ChangeEvent, useRef, useEffect } from "react";
import { SelectBox } from "@root/shared/components/SelectBox";
import { TextField } from "@root/shared/components/TextField";
import { Button } from "@root/shared/components/Button";
import { axios, type AxiosError } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { TextButton } from "@root/shared/components/TextButton";
import * as styles from "./styles.css";

type Option = [string, string];

export type RegistrationCompleteSearchFormProps = {
  formPath: string;
  fetchCitiesListPath: string;
  authenticityToken: string;
  defaultPrefectureId: string | null; // 未選択の場合nullが入る
  defaultCityId: string | null;
  defaultOccupationId: string | null;
  prefectures: Option[];
  cities: Option[];
  occupations: Option[];
  gaLabel?: string;
};

export const RegistrationCompleteSearchForm: FC<RegistrationCompleteSearchFormProps> = ({
  formPath,
  fetchCitiesListPath,
  authenticityToken,
  defaultPrefectureId,
  defaultCityId,
  defaultOccupationId,
  prefectures,
  cities,
  occupations,
  gaLabel,
}) => {
  const prefectureOptions = prefectures.map(([label, value]) => ({ value, label }));
  const citiesOptions = cities.map(([label, value]) => ({ value, label }));
  const occupationOptions = occupations.map(([label, value]) => ({ value, label }));
  const formRef = useRef<HTMLFormElement>(null);

  // states
  const [prefectureId, setPrefectureId] = useState<string | undefined>(
    defaultPrefectureId ?? undefined,
  );
  const [cityId, setCityId] = useState<string | undefined>(defaultCityId ?? undefined);
  const [occupationId, setOccupationId] = useState<string | undefined>(
    defaultOccupationId ?? undefined,
  );
  const [keyword, setKeyword] = useState<string>("");
  const [searchReuse, setSearchReuse] = useState(false);
  const [cityOptions, setCityOptions] = useState(citiesOptions);

  // handlers
  const handleChangePrefecture = (e: ChangeEvent<HTMLSelectElement>) => {
    setPrefectureId(e.target.value);

    // 都道府県を選択したら市区町村リストを更新し、市区町村の選択値をリセットする
    axios
      .get(fetchCitiesListPath, {
        params: { prefecture: e.target.value },
      })
      .then((response) => {
        const citiesList: Option[] = response.data;
        const formattedCityOptions = citiesList.map(([label, value]) => ({ label, value }));
        setCityOptions(formattedCityOptions);
        setCityId(undefined);
      })
      .catch((error: AxiosError) => {
        sendErrorLog(new Error(error?.message || "市区町村のデータを取得できませんでした"));
      });
  };

  const handleChangeCity = (e: ChangeEvent<HTMLSelectElement>) => {
    setCityId(e.target.value);
  };

  const handleChangeOccupation = (e: ChangeEvent<HTMLSelectElement>) => {
    setOccupationId(e.target.value);
  };

  const handleChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const handleDetailSearch = () => {
    // 詳しい条件で探すボタンを押した場合、search_reuseパラメータを付与する
    setSearchReuse(true);
  };

  // effects
  useEffect(() => {
    // search_reuseパラメータが付与された場合、フォームを送信する
    if (searchReuse) {
      formRef.current?.submit();
    }
  }, [searchReuse]);

  return (
    <form ref={formRef} action={formPath} method="POST">
      <input type="hidden" name="authenticity_token" value={authenticityToken} />
      {searchReuse && <input type="hidden" name="search_reuse" value="true" />}
      <div className={styles.outer}>
        <div className={styles.addressFields}>
          <SelectBox
            name="q[pref]"
            id="q_pref"
            value={prefectureId}
            options={prefectureOptions}
            placeholder="都道府県"
            data-ga-action="step2_completed_Top_EasySearch_prefecture"
            selected={!!prefectureId}
            onChange={handleChangePrefecture}
            aria-label="都道府県"
          />
          <SelectBox
            name="q[city][]"
            id="q_city"
            value={cityId}
            options={cityOptions}
            placeholder="市区町村"
            data-ga-action="step2_completed_Top_EasySearch_city"
            selected={!!cityId}
            onChange={handleChangeCity}
            aria-label="市区町村"
          />
        </div>
        <SelectBox
          name="q[business_type][]"
          id="q_business_type"
          value={occupationId}
          options={occupationOptions}
          placeholder="職種"
          data-ga-action="step2_completed_Top_EasySearch_occupation"
          selected={!!occupationId}
          onChange={handleChangeOccupation}
          aria-label="職種"
          className={styles.occupationField}
        />
        <TextField
          name="q[keywords]"
          id="q_keywords"
          placeholder="キーワードを入力してください。"
          value={keyword}
          onChange={handleChangeKeyword}
          aria-label="キーワード"
          className={styles.keywordField}
        />
      </div>
      <div className={styles.buttonGroup}>
        <p className={styles.submitButtonOuter}>
          <Button
            theme="primary"
            type="submit"
            data-ga-action="Top_EasySearch_Button"
            data-ga-label={gaLabel}
          >
            カンタン検索
          </Button>
        </p>
        <p className={styles.detailSearchButtonOuter}>
          <TextButton
            type="button"
            data-ga-action="Top_EasySearch_Button_to_search_form"
            data-ga-label={gaLabel}
            onClick={handleDetailSearch}
            size="large"
            isBold
            rightIconName="arrow-bold-right"
          >
            詳しい条件で探す
          </TextButton>
        </p>
      </div>
    </form>
  );
};
